import { Head, useForm, router } from "@inertiajs/react"
import { Heart, Plus, Pencil, Trash2, Check, Search } from "lucide-react"
import { useState, useMemo } from "react"
import { MemberLayout } from "../components/MemberLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"

function prCompareWithNullsLast(a, b, direction) {
  const aMissing = a === null || a === undefined
  const bMissing = b === null || b === undefined
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  if (a < b) return direction === "asc" ? -1 : 1
  if (a > b) return direction === "asc" ? 1 : -1
  return 0
}
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { timeAgo } from "@/lib/time-ago"
import { CreatedByLine } from "@/components/group-features/CreatedByLine"

function StatusBadge({ status }) {
  if (status === "answered") {
    return (
      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">
        Answered
      </Badge>
    )
  }
  return (
    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200">
      Praying
    </Badge>
  )
}

function CreateEditDialog({ open, onOpenChange, request = null, groupMembers = [], isCoach = false }) {
  const isEditing = !!request
  const form = useForm({
    title: request?.title || "",
    description: request?.description || "",
    account_id: "",
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    if (isEditing) {
      form.patch(`/app/member/prayer_requests/${request.id}`, {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    } else {
      form.post("/app/member/prayer_requests", {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Prayer Request" : "New Prayer Request"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update this prayer request."
                : "Share what's on your heart with your group."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            {isCoach && !isEditing && groupMembers.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="account_id">Create for member (optional)</Label>
                <Select
                  value={form.data.account_id}
                  onValueChange={(value) => form.setData("account_id", value)}
                >
                  <SelectTrigger className="w-full bg-white border-zinc-300">
                    <SelectValue placeholder="Myself" />
                  </SelectTrigger>
                  <SelectContent>
                    {groupMembers.map((member) => (
                      <SelectItem key={member.id} value={String(member.id)}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="What would you like prayer for?"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
              />
              {form.errors.title && (
                <p className="text-sm text-red-600">{form.errors.title}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Share more details..."
                rows={4}
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
              {form.errors.description && (
                <p className="text-sm text-red-600">{form.errors.description}</p>
              )}
            </div>
          </div>

          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? "Saving..."
                : isEditing
                  ? "Save Changes"
                  : "Create Prayer Request"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function DetailDialog({ open, onOpenChange, request, currentUserId, isCoach = false, onEdit, onMarkAnswered, onDelete }) {
  if (!request) return null

  const canModify = request.canManage

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <ResponsiveDialogHeader>
          <div className="flex items-center gap-2">
            <ResponsiveDialogTitle className="font-display text-xl">
              {request.title}
            </ResponsiveDialogTitle>
            <StatusBadge status={request.status} />
          </div>
          <ResponsiveDialogDescription>
            By {request.author.name} &middot; {timeAgo(request.createdAt)}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="space-y-4 py-4 px-4 sm:px-0">
          {request.description && (
            <p className="text-sm text-zinc-700 whitespace-pre-wrap">{request.description}</p>
          )}

          <CreatedByLine author={request.author} createdBy={request.createdBy} />

          {request.status === "answered" && request.howItWasAnswered && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-1">
              <p className="text-xs font-medium text-green-700">How it was answered</p>
              <p className="text-sm text-green-900 whitespace-pre-wrap">{request.howItWasAnswered}</p>
            </div>
          )}
        </div>

        {canModify && (
          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              {request.status === "praying" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onOpenChange(false)
                    onMarkAnswered(request)
                  }}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Mark Answered
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenChange(false)
                  onEdit(request)
                }}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => {
                  onOpenChange(false)
                  onDelete(request)
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </ResponsiveDialogFooter>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function MarkAnsweredDialog({ open, onOpenChange, request }) {
  const form = useForm({
    status: "answered",
    how_it_was_answered: "",
  })

  if (!request) return null

  const handleSubmit = (e) => {
    e.preventDefault()
    form.patch(`/app/member/prayer_requests/${request.id}`, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              Mark as Answered
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Share how God answered this prayer: "{request.title}"
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="how_it_was_answered">Testimony</Label>
              <Textarea
                id="how_it_was_answered"
                placeholder="How was this prayer answered?"
                rows={4}
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.how_it_was_answered}
                onChange={(e) => form.setData("how_it_was_answered", e.target.value)}
              />
            </div>
          </div>

          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing ? "Saving..." : "Mark Answered"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

export default function PrayerRequestsIndex({
  currentUser,
  prayingRequests = [],
  answeredRequests = [],
  userGroupRole,
  groupMembers = [],
}) {
  const isCoach = userGroupRole === "coach"

  // Dialog states
  const [createOpen, setCreateOpen] = useState(false)
  const [createKey, setCreateKey] = useState(0)

  const [editOpen, setEditOpen] = useState(false)
  const [editKey, setEditKey] = useState(0)
  const [editingRequest, setEditingRequest] = useState(null)

  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)

  const [markAnsweredOpen, setMarkAnsweredOpen] = useState(false)
  const [markAnsweredKey, setMarkAnsweredKey] = useState(0)
  const [answeringRequest, setAnsweringRequest] = useState(null)

  const [deleteTarget, setDeleteTarget] = useState(null)

  const handleCreate = () => {
    setCreateKey((k) => k + 1)
    setCreateOpen(true)
  }

  const handleCardClick = (request) => {
    setSelectedRequest(request)
    setDetailOpen(true)
  }

  const handleEdit = (request) => {
    setEditingRequest(request)
    setEditKey((k) => k + 1)
    setEditOpen(true)
  }

  const handleMarkAnswered = (request) => {
    setAnsweringRequest(request)
    setMarkAnsweredKey((k) => k + 1)
    setMarkAnsweredOpen(true)
  }

  const handleDelete = () => {
    if (deleteTarget) {
      router.delete(`/app/member/prayer_requests/${deleteTarget.id}`, {
        preserveScroll: true,
        onSuccess: () => setDeleteTarget(null),
      })
    }
  }

  // Search and filter
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [memberFilter, setMemberFilter] = useState("all")
  // Default: newest submitted first (task #517). Shared across the four
  // group-feature lists.
  const [sortKey, setSortKey] = useState("createdAt")
  const [direction, setDirection] = useState("desc")

  const handleSort = (column) => {
    if (sortKey !== column) {
      setSortKey(column)
      setDirection("asc")
      return
    }
    if (direction === "asc") {
      setDirection("desc")
      return
    }
    setSortKey("createdAt")
    setDirection("desc")
  }

  const allRequests = useMemo(() => [...prayingRequests, ...answeredRequests], [prayingRequests, answeredRequests])

  const memberOptions = useMemo(() => {
    const byId = new Map()
    allRequests.forEach((r) => {
      if (r.author?.id != null && !byId.has(r.author.id)) {
        byId.set(r.author.id, r.author)
      }
    })
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [allRequests])

  const filteredRequests = useMemo(() => {
    let results = allRequests
    if (statusFilter !== "all") {
      results = results.filter((r) => r.status === statusFilter)
    }
    if (memberFilter !== "all") {
      results = results.filter((r) => String(r.author?.id) === memberFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      results = results.filter(
        (r) => r.title.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q)
      )
    }
    const sorted = [...results].sort((a, b) => {
      let aVal
      let bVal
      if (sortKey === "createdAt") {
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : null
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : null
      } else if (sortKey === "title") {
        aVal = a.title?.toLowerCase() || null
        bVal = b.title?.toLowerCase() || null
      } else if (sortKey === "status") {
        aVal = a.status?.toLowerCase() || null
        bVal = b.status?.toLowerCase() || null
      } else if (sortKey === "author") {
        aVal = a.author?.name?.toLowerCase() || null
        bVal = b.author?.name?.toLowerCase() || null
      }
      return prCompareWithNullsLast(aVal, bVal, direction)
    })
    return sorted
  }, [allRequests, statusFilter, memberFilter, searchQuery, sortKey, direction])

  const isEmpty = allRequests.length === 0

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title="Prayer Requests - Kingdom Factor" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Prayer Requests</h1>
            <p className="text-zinc-600 mt-1">
              Lift up your group in prayer.
            </p>
          </div>
          <Button variant="gradient" onClick={handleCreate} className="self-start sm:self-auto">
            <Plus className="h-4 w-4 mr-2" />
            New Prayer Request
          </Button>
        </div>

        {/* Empty state */}
        {isEmpty && (
          <div className="text-center py-12 text-zinc-500">
            <Heart className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium">No prayer requests yet.</p>
            <p className="text-sm mt-1">Share what's on your heart.</p>
          </div>
        )}

        {!isEmpty && (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  placeholder="Search prayer requests..."
                  className="pl-9 bg-white border-zinc-300"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="praying">Praying</SelectItem>
                  <SelectItem value="answered">Answered</SelectItem>
                </SelectContent>
              </Select>
              <Select value={memberFilter} onValueChange={setMemberFilter}>
                <SelectTrigger className="w-full sm:w-44 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  {memberOptions.map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            {filteredRequests.length === 0 ? (
              <p className="text-center py-8 text-zinc-500 text-sm">No matching prayer requests.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader column="title" sortKey={sortKey} direction={direction} onSort={handleSort}>
                      Title
                    </SortableHeader>
                    <SortableHeader column="status" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-24">
                      Status
                    </SortableHeader>
                    <SortableHeader column="author" sortKey={sortKey} direction={direction} onSort={handleSort} className="hidden sm:table-cell">
                      Author
                    </SortableHeader>
                    <SortableHeader column="createdAt" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-24 text-right">
                      Date
                    </SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow
                      key={request.id}
                      className="cursor-pointer"
                      onClick={() => handleCardClick(request)}
                    >
                      <TableCell className="whitespace-normal">
                        <span className="font-medium text-zinc-900 line-clamp-2">{request.title}</span>
                        {request.description && (
                          <p className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{request.description}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={request.status} />
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-zinc-600">
                        {request.author.name}
                      </TableCell>
                      <TableCell className="text-right text-sm text-zinc-500">
                        {timeAgo(request.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>

      {/* Create dialog */}
      <CreateEditDialog
        key={`create-${createKey}`}
        open={createOpen}
        onOpenChange={setCreateOpen}
        groupMembers={groupMembers}
        isCoach={isCoach}
      />

      {/* Edit dialog */}
      <CreateEditDialog
        key={`edit-${editKey}`}
        open={editOpen}
        onOpenChange={setEditOpen}
        request={editingRequest}
        groupMembers={groupMembers}
        isCoach={isCoach}
      />

      {/* Detail dialog */}
      <DetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        request={selectedRequest}
        currentUserId={currentUser.id}
        isCoach={isCoach}
        onEdit={handleEdit}
        onMarkAnswered={handleMarkAnswered}
        onDelete={setDeleteTarget}
      />

      {/* Mark Answered dialog */}
      <MarkAnsweredDialog
        key={`answer-${markAnsweredKey}`}
        open={markAnsweredOpen}
        onOpenChange={setMarkAnsweredOpen}
        request={answeringRequest}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prayer Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.title}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MemberLayout>
  )
}
